import {
  ImageCoverView,
  ImageScaleView,
  ImageView,
  LinearAddFilter,
  View,
} from '@play-co/timestep-core/ui';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import { Match2RewardItem } from './Match2RewardItem';
import StateObserver, { user, now } from 'src/StateObserver';
import { gsap } from 'gsap';
import sounds from 'src/lib/sounds';
import {
  getMatch2FrenzyTimerText,
  createRewardCompensation,
  updateRewardCompensation,
  RewardCompensation,
} from '../match2.util';
import { createWaiter } from 'src/lib/utils';
import { duration } from 'src/replicant/utils/duration';
import { getCurrentPetState } from 'src/replicant/fate/fate.getters';
import { captureGenericError } from 'src/lib/sentry';
import { openMatch2FrenzyIntroPopup } from '../frenzy/match2-frenzy.sequence';
import { formatDuration } from 'src/lib/formatDuration';
import { getMatch2TimedRewardRemainingTime } from 'src/replicant/match2/match2.getters';
import {
  getMatch2FrenzyEventConfig,
  getMatch2FrenzyMilestone,
  getMatch2FrenzyMilestoneLevel,
  getMatch2FrenzyMilestoneProgress,
  getMatch2FrenzyMilestoneReward,
  hasCompletedMatch2Frenzy,
} from 'src/replicant/match2/frenzy/match2-frenzy.getters';
import {
  getMatch2RewardDecoration,
  openMatch2ChestPopup,
} from '../rewards/match2-rewards.utils';
import { Match2Reward } from 'src/replicant/match2/rewards/match2-rewards.ruleset';
import { processMatch2Rewards } from 'src/replicant/match2/rewards/match2-rewards.getters';
import Match2ProgressBar from './Match2ProgressBar';
import { getFrenzyObjectiveIcon } from '../match2.icons';
import { Match2FrenzyTheme } from 'src/replicant/match2/frenzy/match2-frenzy.ruleset';
import { setCompensation } from 'src/game/inventory/inventory.redux';
import { AssetGroup } from '@play-co/timestep-core';

let progressStamp = 0;

export default class Match2FrenzyBar extends Match2ProgressBar {
  private timer: LangBitmapFontTextView;
  private objectiveBackground: ImageView;
  private multiplierBanner: ImageScaleView;
  private multiplierTimer: LangBitmapFontTextView;
  private glareFx: ImageScaleView;
  private rewardIcon: Match2RewardItem;
  private progressText: LangBitmapFontTextView;
  private _current = 0;
  private requirement = 0;
  private waiter?: ReturnType<typeof createWaiter>;
  private animating = false;
  private currentConfigId?: string;

  constructor({
    superview,
    width = 380,
    x = 0,
    y = 0,
    scale = 1,
    visible = true,
    syncState = false,
    canHandleEvents = true,
  }: {
    superview: View;
    width?: number;
    x?: number;
    y?: number;
    scale?: number;
    visible?: boolean;
    syncState?: boolean;
    canHandleEvents?: boolean;
  }) {
    super({
      superview,
      width,
      height: 58,
      x,
      y,
      scale,
      canHandleEvents,
    });

    // Open the Frenzy intro popup when the user taps on the progress bar, only if pet is not dead
    this.onInputStart = () => {
      // Prevent tap while the menu is locked
      if (StateObserver.getState().ui.lockMenu) return;

      const user = StateObserver.getState().user;
      const now = StateObserver.now();

      // Prevent tap while pet is dead or the bar is animating
      if (getCurrentPetState(user, now) === 'dead' || this.animating) return;
      openMatch2FrenzyIntroPopup({ origin: 'hud' });
    };

    this.updateOpts({ visible, anchorY: 29 });

    const subPanel = new ImageView({
      superview: this,
      image: 'assets/ui/match2/frenzy/common/panel_frenzy_timer.png',
      width: 208,
      height: 41,
      canHandleEvents: false,
      x: this.style.width * 0.5,
      y: 70,
      centerOnOrigin: true,
      centerAnchor: true,
    });

    this.timer = new LangBitmapFontTextView({
      superview: subPanel,
      font: 'Body',
      color: '#783B3D',
      width: subPanel.style.width * 0.95,
      size: 18,
      x: subPanel.style.width * 0.5,
      y: 7,
      centerOnOrigin: true,
      centerAnchor: true,
      align: 'center',
      verticalAlign: 'center',
    });

    this.progressText = new LangBitmapFontTextView({
      superview: this,
      font: 'Title',
      color: '#FFFFFF',
      size: 25,
      x: this.style.width * 0.5,
      y: 12,
      centerOnOrigin: true,
      centerAnchor: true,
      align: 'center',
      verticalAlign: 'center',
    });

    this.objectiveBackground = new ImageCoverView({
      superview: this,
      width: 86 * 0.9,
      height: 80 * 0.9,
      mode: 'contain',
      centerOnOrigin: true,
      centerAnchor: true,
      x: 15,
      y: this.style.height * 0.5 - 4,
    });

    this.multiplierBanner = new ImageScaleView({
      superview: this.objectiveBackground,
      image: 'assets/ui/match2/frenzy/common/badge_frenzy_default.png',
      width: 80,
      height: 37,
      centerOnOrigin: true,
      centerAnchor: true,
      x: this.objectiveBackground.style.width * 0.5 - 8,
      y: this.objectiveBackground.style.height - 3,
      scale: 1.15,
      visible: false,
    });

    this.multiplierTimer = new LangBitmapFontTextView({
      parent: this.multiplierBanner,
      font: 'Title',
      color: '#FFFFFF',
      size: 17,
      x: this.multiplierBanner.style.width * 0.5 - 2,
      y: 6,
      centerOnOrigin: true,
      verticalAlign: 'center',
      align: 'center',
    });

    new LangBitmapFontTextView({
      parent: this.multiplierBanner,
      font: 'Title',
      color: '#FFFFFF',
      size: 23,
      x: 61,
      y: -28,
      centerOnOrigin: true,
      localeText: () => 'x2', // currently hardcoded as only x2 multiplier exists
      verticalAlign: 'center',
      align: 'center',
    });

    this.glareFx = new ImageScaleView({
      superview: this,
      image: 'assets/ui/match2/common/fx_shine.png',
      width: 344,
      height: 282,
      x: this.style.width,
      y: this.style.height * 0.5 - 4,
      centerOnOrigin: true,
      centerAnchor: true,
      filter: new LinearAddFilter('rgba(255, 255, 255, 0.5)'),
      visible: false,
    });

    this.rewardIcon = new Match2RewardItem({
      superview: this,
      centerOnOrigin: true,
      centerAnchor: true,
      x: this.style.width,
      y: this.style.height * 0.5 - 4,
      scale: 0.65,
    });

    this.currentConfigId = this.getCurrentConfig()?.id;
    if (syncState) this.syncState();

    void this.loadAndShow();
  }

  /**
   * Will make the bar visible only after the required assets are loaded.
   */
  private async loadAndShow() {
    this.updateOpts({ opacity: 0 });
    const assets = new AssetGroup([
      'assets/ui/match2/common/bar_frame_green.png',
      'assets/ui/match2/common/bar_fill_green.png',
      ...AssetGroup.constructURLs(['assets/ui/match2/frenzy/common/']),
    ]);
    await assets.load();
    gsap.to(this.style, {
      opacity: 1,
      duration: 0.3,
      ease: 'linear',
    });
  }

  private getCurrentConfig() {
    const { user } = StateObserver.getState();
    const now = StateObserver.now();
    return getMatch2FrenzyEventConfig(user, now);
  }

  private getRemainingFrenzyMultiplierTime() {
    return getMatch2TimedRewardRemainingTime(
      user(),
      'multiplier-frenzy',
      now(),
    );
  }

  private refreshMultiplierBanner() {
    const remaining = this.getRemainingFrenzyMultiplierTime();
    this.multiplierBanner.style.visible = remaining > 0;
    if (remaining > 0) {
      this.multiplierTimer.localeText = () => formatDuration(remaining);
    }
  }

  public autoUpdate() {
    this.timer.localeText = () => getMatch2FrenzyTimerText();
    this.refreshMultiplierBanner();

    // Auto sync if the current config has changed. Only happens when changing events during the session
    const config = this.getCurrentConfig();
    if (!this.animating && this.currentConfigId !== config?.id) {
      StateObserver.invoke.resetMatch2Frenzy().then(() => {
        this.syncState();
      });
      this.currentConfigId = config?.id;
    }

    this.waiter = createWaiter();
    this.waiter.wait(this.autoUpdate.bind(this), duration({ seconds: 1 }));
  }

  public stopAutoUpdate() {
    this.waiter?.clear();
    this.waiter = undefined;
  }

  public syncState() {
    const { user } = StateObserver.getState();
    const now = StateObserver.now();
    const config = getMatch2FrenzyEventConfig(user, now);
    const milestone = getMatch2FrenzyMilestone(user, now);
    const progress = getMatch2FrenzyMilestoneProgress(user, now);
    const reward = getMatch2FrenzyMilestoneReward(user, now);

    if (!config || !milestone || !reward) {
      // Send error to Sentry instead of throwing error, to not prevent the app from starting
      // This can happen if we set the game to a few days before, in cheats
      captureGenericError(
        'Match2FrenzyError',
        'Match2 frenzy config not found!',
      );
      return;
    }

    this.setObjective(config.theme);
    this.setReward(reward);
    this.setRequirement(milestone.requirement);
    this.refreshMultiplierBanner();
    this.current = progress;

    this.style.scaleY = Number(!hasCompletedMatch2Frenzy(user, now));

    // Save current progress for animating state change
    progressStamp = user.match2.frenzy.progress;
  }

  public refreshState() {
    const { user } = StateObserver.getState();
    const now = StateObserver.now();
    const config = getMatch2FrenzyEventConfig(user, now);
    const milestone = getMatch2FrenzyMilestone(user, now, progressStamp);
    const progress = getMatch2FrenzyMilestoneProgress(user, now, progressStamp);
    const level = getMatch2FrenzyMilestoneLevel(user, now, progressStamp);
    const reward = getMatch2FrenzyMilestoneReward(user, now, level);

    if (!config || !milestone || !reward) {
      throw new Error('Match2 frenzy config not found!');
    }

    this.setObjective(config.theme);
    this.setReward(reward);
    this.setRequirement(milestone.requirement);
    this.current = progress;
  }

  public async animateState(rewards: Match2Reward[]) {
    const { user } = StateObserver.getState();
    const now = StateObserver.now();
    const config = getMatch2FrenzyEventConfig(user, now);

    if (!config) {
      throw new Error('Match2 frenzy config not found!');
    }

    const compensation = createRewardCompensation();
    rewards.forEach((reward) => {
      updateRewardCompensation(compensation, reward, 1);
    });
    StateObserver.dispatch(setCompensation(compensation));

    // Add an interaction Blocker
    const blocker = new View({
      superview: this.getApp() as View,
      infinite: true,
      canHandleEvents: true,
    });

    try {
      const previousMilestoneLevel = getMatch2FrenzyMilestoneLevel(
        user,
        now,
        progressStamp,
      );
      const currentMilestoneLevel = getMatch2FrenzyMilestoneLevel(user, now);
      const currentProgress = getMatch2FrenzyMilestoneProgress(user, now);
      const completed = hasCompletedMatch2Frenzy(user, now);

      // applaud if we have reached a new milestone
      if (previousMilestoneLevel <= currentMilestoneLevel) {
        sounds.sfx.play('applause');
      }

      this.animating = true;

      for (
        let index = previousMilestoneLevel;
        index <= currentMilestoneLevel;
        index++
      ) {
        const { requirement } = config.progression[index];

        await gsap.to(this, {
          duration: 1,
          current:
            completed || index < currentMilestoneLevel
              ? requirement
              : currentProgress,
          ease: 'quad.inOut',
        });

        if (index < currentMilestoneLevel || completed) {
          const reward = this.rewardIcon.getReward();
          const decoration = getMatch2RewardDecoration(reward);
          const isChestMode = decoration?.type === 'chest';

          // basic reward animation
          if (!isChestMode) {
            this.rewardIcon.grantReward(this.getParents().pop() as View);
          }

          await gsap.to(this.rewardIcon.style, {
            duration: 0.1,
            scale: 0.5,
            ease: 'quad.inOut',
          });
          gsap
            .timeline()
            .to(this.rewardIcon.style, {
              duration: 0.3,
              scale: 1,
              ease: 'quad.inOut',
            })
            .to(
              this.rewardIcon.style,
              { duration: 0.3, opacity: 0, ease: 'quad.in' },
              0,
            );
          sounds.sfx.play('milestone');
          await this.glare();

          // chest reward popup
          if (isChestMode) {
            // Disable interaction blocker while showing the reward popup
            blocker.updateOpts({ visible: false });
            const rewards = processMatch2Rewards([reward], now);
            await openMatch2ChestPopup({
              rewards,
              chestType: decoration?.subType,
              origin: undefined,
            });
            blocker.updateOpts({ visible: true });
          }

          await gsap.to(this.style, {
            duration: 0.2,
            scaleY: 0,
            ease: 'quad.inOut',
          });

          // Revert the compensation after the reward is granted
          updateRewardCompensation(compensation, reward, -1);
          StateObserver.dispatch(setCompensation(compensation));

          // If we have reached the last milestone, we keep the progress bar invisible.
          if (index === currentMilestoneLevel && completed) return;

          const newRequirement = config.progression[index + 1].requirement;
          const newReward = getMatch2FrenzyMilestoneReward(
            user,
            now,
            index + 1,
          );

          this.current = 0;
          this.setRequirement(newRequirement);
          if (newReward) this.setReward(newReward);
          this.rewardIcon.style.scale = 0.65;
          this.rewardIcon.style.opacity = 1;
          this.refreshMultiplierBanner();

          await gsap.to(this.style, {
            duration: 0.2,
            scaleY: 1,
            ease: 'quad.inOut',
          });
        }
      }
    } finally {
      // Save current progress for animating state change
      progressStamp = user.match2.frenzy.progress;

      this.animating = false;

      // Remove the interaction blocker
      blocker.removeFromSuperview();
    }
  }

  private async glare() {
    gsap.killTweensOf(this.glareFx.style);
    this.glareFx.style.opacity = 1;
    this.glareFx.style.scale = 0;
    this.glareFx.style.visible = true;

    const duration = 0.3;

    await gsap
      .timeline()
      .to(this.glareFx.style, {
        duration: duration,
        scale: 0.85,
        ease: 'quad.out',
      })
      .to(
        this.glareFx.style,
        {
          duration,
          opacity: 0,
          ease: 'quad.in',
        },
        0,
      );

    this.glareFx.style.visible = false;
  }

  public set current(v: number) {
    this._current = v;
    this.refresh();
  }

  public get current() {
    return this._current;
  }

  public setObjective(theme: Match2FrenzyTheme) {
    getFrenzyObjectiveIcon(theme)
      .get()
      .then((image) => {
        this.objectiveBackground.setImage(image);
      });
  }

  public setReward(reward: Match2Reward) {
    this.rewardIcon.setReward(reward);
  }

  public setRequirement(v: number) {
    this.requirement = v;
    this.refresh();
  }

  private refresh() {
    const current = Math.min(this.current, this.requirement);
    this.progress = current / this.requirement;
    if (!this.progressText) return;
    this.progressText.localeText = () =>
      `${Math.round(current)}/${this.requirement}`;
  }

  public get fillWidthRatio() {
    const max = (this.style.width ?? 497) - this.paddingRight;
    return this.progressFill.style.width / max;
  }

  public getObjectiveIconPos(view: View) {
    const position = this.objectiveBackground.getPosition(view);
    position.x += this.objectiveBackground.style.width * 0.5;
    position.y += this.objectiveBackground.style.height * 0.5;
    return position;
  }
}
